'use client'

import { cn } from '@nt/ui/cn'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const labelVariants = cva(
  'gap-2 flex items-center select-none group-data-[disabled=true]:opacity-50 group-data-[disabled=true]:pointer-events-none peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
  {
    variants: {
      tone: {
        // A field label is the ink (company.css `.ct-field label`).
        default: 'text-foreground',
        brand: 'text-primary',
        muted: 'text-muted-foreground',
        subtle: 'text-muted-foreground',
        // Colour that carries meaning is never normalised away (spec D3):
        // form-1042's Balance Due / Overpayment pair.
        critical: 'text-danger',
        success: 'text-success',
        // A deadline or something to act on (never an error).
        warning: 'text-warning',
      },
      size: {
        // leading-none sits after the text-* class in each variant: tailwind-merge
        // treats font-size as conflicting with line-height, so an earlier
        // leading-none gets dropped when a later text-* class group is applied.
        sm: 'text-sm leading-none font-medium',
        xs: 'text-compact leading-none font-medium',
        base: 'text-base leading-none font-medium',
        // The small label above a group: sentence case, no letter-spaced caps.
        eyebrow: 'text-compact leading-4 font-medium',
      },
    },
    defaultVariants: { tone: 'default', size: 'sm' },
  },
)

function Label({
  className,
  tone,
  size,
  ...props
}: React.ComponentProps<'label'> & VariantProps<typeof labelVariants>) {
  return <label data-slot="label" className={cn(labelVariants({ tone, size, className }))} {...props} />
}

export { Label, labelVariants }
